import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { downloadPdf } from 'lib/web/downloadPdf'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import {
  buildTripListRows,
  filterTripListRows,
  groupTripListRows,
  sortTripListRows,
} from '../components/ListView/tripListRows'
import { buildTripListCsv } from '../components/ListView/tripListCsv'
import type {
  TripListFilters,
  TripListRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
} from '../components/ListView/tripListTypes'
import TripListView from '../components/ListView/TripListView'

interface Props {
  visible: boolean
}

const DEFAULT_FILTERS: TripListFilters = {
  types: new Set<TripListRowType>(),
  bookableOnly: false,
}

function getNextSort(sort: TripListSort | undefined, field: TripListSortField): TripListSort | undefined {
  if (sort?.field !== field) {
    return { field, direction: 'asc' }
  }

  if (sort.direction === 'asc') {
    return { field, direction: 'desc' }
  }

  return undefined
}

function ListContainer({ visible }: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { itineraryData, deleteManualItineraryTarget, isManualItineraryDeletePending } = useItineraryContext()
  const actionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending

  const [sort, setSort] = useState<TripListSort | undefined>(undefined)
  const [filters, setFilters] = useState<TripListFilters>(DEFAULT_FILTERS)
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set())
  const [luxPlusEnabled, setLuxPlusEnabled] = useState(true)

  const allRows = useMemo(() => buildTripListRows(itineraryData, intl), [intl, itineraryData])
  const visibleRows = useMemo(() => {
    return sortTripListRows(filterTripListRows(allRows, filters), sort)
  }, [allRows, filters, sort])
  const groups = useMemo(() => groupTripListRows(visibleRows, sort, intl), [intl, sort, visibleRows])

  useEffect(() => {
    setSelectedRowIds(prev => {
      const visibleIds = new Set(visibleRows.map(row => row.id))
      const next = new Set([...prev].filter(id => visibleIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleRows])

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedRowIds(checked ? new Set(visibleRows.map(row => row.id)) : new Set())
  }, [visibleRows])

  const handleSelectRow = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(rowId)
      } else {
        next.delete(rowId)
      }
      return next
    })
  }, [])

  const handleSelectRows = useCallback((rowIds: Array<string>, checked: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      for (const rowId of rowIds) {
        if (checked) {
          next.add(rowId)
        } else {
          next.delete(rowId)
        }
      }
      return next
    })
  }, [])

  const handleTypeFilterToggle = useCallback((type: TripListRowType) => {
    setFilters(prev => {
      const types = new Set(prev.types)
      if (types.has(type)) {
        types.delete(type)
      } else {
        types.add(type)
      }
      return { ...prev, types }
    })
  }, [])

  const handleBookableOnlyToggle = useCallback(() => {
    setFilters(prev => ({ ...prev, bookableOnly: !prev.bookableOnly }))
  }, [])

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
  }, [])

  const handleSortToggle = useCallback((field: TripListSortField) => {
    setSort(prev => getNextSort(prev, field))
  }, [])

  const handleSortChange = useCallback((nextSort: TripListSort | undefined) => {
    setSort(nextSort)
  }, [])

  const handleLuxPlusToggle = useCallback(() => {
    setLuxPlusEnabled(prev => !prev)
  }, [])

  const handleDownloadCsv = useCallback(() => {
    const csv = buildTripListCsv(visibleRows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    downloadPdf(blob, 'trip-planner-itinerary.csv')
  }, [visibleRows])

  const handleRowClick = useCallback((row: TripListRow) => {
    if (row.offerTarget) {
      eventBus.dispatch({
        type: 'open_offer_drawer',
        offerId: row.offerTarget.offerId,
        offerType: row.offerTarget.offerType,
        searchParams: row.offerTarget.searchParams,
      })
      return
    }

    if (row.placeTarget) {
      eventBus.dispatch({
        type: 'open_place_drawer',
        googlePlaceId: row.placeTarget.googlePlaceId,
        placeName: row.placeTarget.placeName,
      })
    }
  }, [eventBus])

  const handleAskAi = useCallback((row: TripListRow) => {
    const visibleMessage = intl.formatMessage(
      {
        id: 'KE2ouyewLoiZzaBt9mKr',
        defaultMessage: 'Tell me more about "{title}"',
        description: 'Chat message sent when asking AI about a row in the trip planner list view',
      },
      { title: row.title },
    )
    const hiddenContext = row.kind === 'item'
      ? `Ask AI about item_id=${row.item.id} ("${row.title}")`
      : `Ask AI about item_id=${row.option.itemId} ("${row.title}")`
    sendMessage(appendContextTag(visibleMessage, hiddenContext, TRIP_PLANNER_ACTION_CONTEXT_LABEL))
  }, [intl, sendMessage])

  const handleDelete = useCallback((row: TripListRow) => {
    if (actionsDisabled) return
    if (row.kind === 'transport') {
      deleteManualItineraryTarget({ targetType: 'item', itemId: row.option.itemId })
      return
    }

    deleteManualItineraryTarget({ targetType: 'item', itemId: row.item.id })
  }, [actionsDisabled, deleteManualItineraryTarget])

  if (!visible) return null

  return (
    <TripListView
      groups={groups}
      allRows={allRows}
      visibleRows={visibleRows}
      selectedRowIds={selectedRowIds}
      filters={filters}
      sort={sort}
      luxPlusEnabled={luxPlusEnabled}
      onSelectAll={handleSelectAll}
      onSelectRows={handleSelectRows}
      onSelectRow={handleSelectRow}
      onTypeFilterToggle={handleTypeFilterToggle}
      onBookableOnlyToggle={handleBookableOnlyToggle}
      onClearFilters={handleClearFilters}
      onSortToggle={handleSortToggle}
      onSortChange={handleSortChange}
      onLuxPlusToggle={handleLuxPlusToggle}
      onDownloadCsv={handleDownloadCsv}
      onRowClick={handleRowClick}
      onAskAi={handleAskAi}
      onDelete={handleDelete}
      actionsDisabled={actionsDisabled}
    />
  )
}

export default ListContainer
